import {
    ensureObject,
    ensureString,
    ensureUpperString,
    formatDateBr,
    fromRadioLike,
    isValidUrl,
    limitLength,
    mapFrequencyToInt,
    mapGarantiasField,
    normalizeDoc,
    pickCaseInsensitive,
    pickValor,
    toDecimal,
    toDecimalComma,
    toInt,
    toISODate,
    toRadioInt,
} from "../utils/convert-fields"

export const mapCovenantsToRows = (covenants = []) => {
    if (!Array.isArray(covenants)) return []
    return covenants.map((cov, idx) => ({
        apiId: cov.id || cov.ID || cov.Id || null,
        id:
            cov.id ||
            cov.ID ||
            cov.Id ||
            `${Date.now()}-${idx}-${Math.random().toString(16).slice(2)}`,
        dataValidadeInicial: formatDateBr(cov.DataValidadeInicial),
        dataValidadeFinal: formatDateBr(cov.DataValidadeFinal),
        tipoCovenant: ensureString(cov.TipoCovenant),
        clausula: ensureString(cov.Clausula),
        numerador: ensureString(cov.Covenant_Numerador),
        denominador: ensureString(cov.Covenant_Denominador),
        limite: ensureString(cov.Limite),
        observacao: ensureString(cov.Observacao),
    }))
}

const normalizeField = (val) => {
    const text = ensureString(val).trim()
    return text ? text : null
}

export const calcularDatasDerivadas = (caracteristica, datasAtuais = {}) => {
    const resultado = {...datasAtuais}

    const regras = [
        {
            campo: "dataInicioPagamentoJuros",
            campoOrigem: "DataInicioPagamentoJuros",
            campoBase: "DataPrimeiraIntegralizacao",
            calcular: (base) => addMonths(base, 1)
        },
        {
            campo: "dataInicioPagamentoAmortizacao",
            campoOrigem: "DataInicioPagamentoAmortizacao",
            campoBase: "DataPrimeiraIntegralizacao",
            calcular: (base) => addMonths(base, 1)
        },
        {
            campo: "dataCarenciaRecompra",
            campoOrigem: "DataCarenciaRecompra",
            campoBase: "DataPrimeiraIntegralizacao",
            calcular: (base) => addMonths(base, 1)
        }
    ]

    regras.forEach(({campo, campoOrigem, campoBase, calcular}) => {
        if (!caracteristica[campoOrigem] && caracteristica[campoBase]) {
            const base = new Date(caracteristica[campoBase])
            const calculada = calcular(base)
            resultado[campo] = formatDateBr(calculada.toISOString().split("T")[0])
        } else {
            resultado[campo] = formatDateBr(caracteristica[campoOrigem])
        }
    })

    return resultado
}

const addMonths = (date, months) => {
    return new Date(date.getFullYear(), date.getMonth() + months, date.getDate())
}

export const calcularCamposDerivados = (caracteristica) => {
    const indexador = caracteristica?.Indexador ?? ""
    const remuneracao = caracteristica?.Remuneracao ?? 0

    const isPercentual = indexador === "CDI_Percentual" || indexador === "SELIC_Percentual"

    return {
        taxaJuros: isPercentual ? 0 : remuneracao,
        indexadorCorrecaoPercentual: isPercentual ? remuneracao : 100,
    }
}

export const mapCovenantRowToPayload = (row = {}) => ({
    DataValidadeInicial: toISODate(row.dataValidadeInicial),
    DataValidadeFinal: toISODate(row.dataValidadeFinal),
    TipoCovenant: normalizeField(row.tipoCovenant),
    Clausula: normalizeField(row.clausula),
    Covenant_Numerador: normalizeField(row.numerador),
    Covenant_Denominador: normalizeField(row.denominador),
    Limite: normalizeField(row.limite),
    Observacao: normalizeField(row.observacao),
})

export const mapCadastroTemporarioPayload = ({
                                                 caracteristicasState = {},
                                                 ofertaState = {},
                                                 garantiasState = {},
                                                 covenantsRows = [],
                                             }) => {
    const caracteristica = ensureObject(caracteristicasState)
    const cetipValue = ensureUpperString(caracteristica.cetip)
    const oferta = ensureObject(ofertaState)
    const garantias = ensureObject(garantiasState)

    const caracteristicaPayload = {
        CETIP: cetipValue,
        VersaoDocumento: toInt(caracteristica.versaoDocumento) ?? 0,
        Caracteristica: {
            CETIP: cetipValue,
            VersaoDocumento: toInt(caracteristica.versaoDocumento) ?? 0,
            IDEmissao: caracteristica.emissao || null,
            DataReferencia: toISODate(caracteristica.dataReferencia),
            DataDocumento: toISODate(caracteristica.dataDocumento),
            ISIN: caracteristica.isin || null,
            NumeroSerie: limitLength(caracteristica.numeroSerie, 4),
            NumeroEmissao: limitLength(caracteristica.numeroEmissao, 4),
            TipoAtivo: caracteristica.tipoAtivo || null,
            TipoOferta: caracteristica.tipoOferta || null,
            SiteFiduciario: isValidUrl(caracteristica.siteFiduciario)
                ? caracteristica.siteFiduciario
                : null,
            FrequenciaJuros: mapFrequencyToInt(caracteristica.frequenciaJuros),
            FrequenciaAmortizacao: mapFrequencyToInt(caracteristica.frequenciaAmortizacao),
            ResgateAntecipado: toRadioInt(caracteristica.resgateAntecipado),
            DataEmissao: toISODate(caracteristica.dataEmissao),
            DataPrimeiraIntegralizacao: toISODate(caracteristica.dataIntegralizacao),
            DataVencimento: toISODate(caracteristica.dataVencimento),
            DataCarenciaResgate: toISODate(caracteristica.dataCarencia),
            DataResgateAntecipado: toISODate(caracteristica.dataResgateAntecipado),
            DataVencimentoAntecipado: toISODate(caracteristica.dataVencimentoAntecipado),
            Indexador: caracteristica.indexador || null,
            Remuneracao: toDecimal(caracteristica.remuneracao) ?? null,
            PUEmissao: toDecimal(caracteristica.puEmissao) ?? null,
            SpreadEmissao: toDecimal(caracteristica.spreadEmissao) ?? null,
            PrazoMedioEmissao: toDecimal(caracteristica.prazoMedio) ?? null,
            PrazoEmissao: toDecimal(caracteristica.prazoEmissao) ?? null,
            QtdEmitida: toDecimal(caracteristica.qtdEmitida) ?? null,
            ValorSerie: toDecimal(caracteristica.valorSerie) ?? null,
            ValorTotalEmissao: toDecimal(caracteristica.valorTotalEmissao) ?? null,
            CotaSubordinacao: caracteristica.cotaSubordinacao || null,
            CascataPagamento: caracteristica.cascataPagamento || null,
            LiquidezDiaria: toInt(caracteristica.liquidezDiaria ?? caracteristica.liquidacaoDiaria),
            Conversibilidade: toRadioInt(caracteristica.conversibilidade),
            Perpetuidade:toRadioInt(caracteristica.perpetuidade),
            Incentivada: toRadioInt(caracteristica.incentivada),
            Green: toRadioInt(caracteristica.green),
            Lei12431: toRadioInt(caracteristica.lei12431),
            Lei14801: toRadioInt(caracteristica.lei14801),
            PrePagamento: toRadioInt(caracteristica.prePagamento),
            GarantiasReais: toRadioInt(caracteristica.garantiasReais),
            GarantiasFidejussorias: toRadioInt(caracteristica.garantiasFidejussorias),
            MitigadorRiscoCredito: toRadioInt(caracteristica.mitigadorRisco),
            Covenants: toRadioInt(caracteristica.covenants),
            RepactuacaoCredito: toRadioInt(caracteristica.repactuacaoCredito),
            CPFCNPJ_AgenteFiduciario: normalizeDoc(caracteristica.agenteFiduciario),
            CPFCNPJ_IFContaRecebedora: normalizeDoc(caracteristica.contaRecebedora),
            CPFCNPJ_Registrador: normalizeDoc(caracteristica.registrador),
            CPFCNPJ_Depositario: normalizeDoc(caracteristica.depositario),
            CPFCNPJ_Custodiante: normalizeDoc(caracteristica.custodiante),
            CPFCNPJ_Auditoria: normalizeDoc(caracteristica.auditoria),
            CPFCNPJ_AgenciaRating: normalizeDoc(caracteristica.agenciaRating),
            CPFCNPJ_Emissor: normalizeDoc(caracteristica.emissor),
            CPFCNPJ_Escriturador: normalizeDoc(caracteristica.escriturador),
            CPFCNPJ_Cedente: normalizeDoc(caracteristica.cedente),
            CPFCNPJ_Devedor: normalizeDoc(caracteristica.devedor),
            ResumoDevedor: caracteristica.resumoDevedor || null,
            Setor: caracteristica.setorDevedor || null,
            Subsetor: caracteristica.subSetorDevedor || null,
            DestinacaoRecursos: caracteristica.destinacaoRecursos || null,
            Coobrigacao: caracteristica.cobrigacao || null,
        },
        Oferta: {
            Distribuicao: oferta.distribuicao || null,
            LinkSRE: oferta.linkSre || null,
            SituacaoDistribuicao: oferta.situacaoDistribuicao || null,
            SituacaoOferta: oferta.situacaoOferta || null,
            TipoMercado: oferta.tipoMercado || null,
            TipoRito: oferta.tipoRito || null,
            Bookbuilding: oferta.bookbuilding || null,
            RemuneracaoBookbuilding: oferta.remuneracaoBookbuilding || null,
            RegimeDistribuicao: oferta.regimeDistribuicao || null,
            PublicoAlvoInvestidorGeralInicial: oferta.investidorGeralInicial || null,
            PublicoAlvoInvestidorQualificadoInicial: oferta.investidorQualificadoInicial || null,
            PublicoAlvoInvestidorProfissionalInicial: oferta.investidorProfissionalInicial || null,
            PublicoAlvoInvestidorGeralFinal: oferta.investidorGeralFinal || null,
            PublicoAlvoInvestidorQualificadoFinal: oferta.investidorQualificadoFinal || null,
            PublicoAlvoInvestidorProfissionalFinal: oferta.investidorProfissionalFinal || null,
            DataRequerimento: toISODate(oferta.dataRequerimento),
            DataRegistro: toISODate(oferta.dataRegistro),
            DataInicioOferta: toISODate(oferta.dataInicioOferta),
            DataEncerramentodaOfertaMaximo: toISODate(oferta.dataEncerramentoMax),
            DataEncerramentodaOfertaEfetivo: toISODate(oferta.dataEncerramentoEfetivo),
            DataInicioReserva: toISODate(oferta.dataInicioReserva),
            DatasEncerramentoReserva: toISODate(oferta.dataEncerramentoReserva),
            DataBookbuilding: toISODate(oferta.dataBookbuilding),
            DataLockupIGparaIG: toISODate(oferta.dataLockupIGparaIG),
            DataLockupIGparaIQ: toISODate(oferta.dataLockupIGparaIQ),
            DataLockupIGparaIP: toISODate(oferta.dataLockupIGparaIP),
            DataLockupIQparaIG: toISODate(oferta.dataLockupIQparaIG),
            DataLockupIQparaIQ: toISODate(oferta.dataLockupIQparaIQ),
            DataLockupIQparaIP: toISODate(oferta.dataLockupIQparaIP),
            DataLockupIPparaIG: toISODate(oferta.dataLockupIPparaIG),
            DataLockupIPparaIQ: toISODate(oferta.dataLockupIPparaIQ),
            DataLockupIPparaIP: toISODate(oferta.dataLockupIPparaIP),
            CPFCNPJ_CoordenadorLider: normalizeDoc(oferta.coordLider),
            CPFCNPJ_Coordenador1: normalizeDoc(oferta.coord1),
            CPFCNPJ_Coordenador2: normalizeDoc(oferta.coord2),
            CPFCNPJ_Coordenador3: normalizeDoc(oferta.coord3),
            CPFCNPJ_Coordenador4: normalizeDoc(oferta.coord4),
            CPFCNPJ_Coordenador5: normalizeDoc(oferta.coord5),
            CPFCNPJ_Coordenador6: normalizeDoc(oferta.coord6),
            CPFCNPJ_Coordenador7: normalizeDoc(oferta.coord7),
        },
        Garantias: {
            TipoLastro: garantias.tipoLastro || null,
            ContaVinculada: toRadioInt(garantias.contaVinculada),
            CotasInferiores: garantias.cotasInferiores || null,
            AlienacaoFiduciaria: garantias.alienacao || null,
            CessaoFiduciaria: garantias.cessao || null,
            FiancaBancaria: garantias.fiancaBancaria || null,
            Hipoteca: garantias.hipoteca || null,
            Penhor: garantias.penhor || null,
            Avalista: garantias.avalista || null,
            Fianca: garantias.fianca || null,
            FundoObra: garantias.fundoObra || null,
            FundoReserva: garantias.fundoReserva || null,
            FundoLiquidez: garantias.fundoLiquidez || null,
            Seguro: garantias.seguro || null,
            Sobregarantia: garantias.sobreGarantia || null,
            PatrimonioSeparado: garantias.patrimonioSeparado || null,
        },
        LTV: {
            LTVNumerador: toDecimal(pickValor(garantias, "ltvNumerador")),
            LTVDenominador: toDecimal(pickValor(garantias, "ltvDenominador")),
            LTV: toDecimal(pickValor(garantias, "ltv")),
            CotasInferiores: toDecimal(pickValor(garantias, "cotasInferiores")),
            AlienacaoFiduciaria: toDecimal(pickValor(garantias, "alienacao")),
            CessaoFiduciaria: toDecimal(pickValor(garantias, "cessao")),
            FiancaBancaria: toDecimal(pickValor(garantias, "fiancaBancaria")),
            Hipoteca: toDecimal(pickValor(garantias, "hipoteca")),
            Penhor: toDecimal(pickValor(garantias, "penhor")),
            Avalista: toDecimal(pickValor(garantias, "avalista")),
            Fianca: toDecimal(pickValor(garantias, "fianca")),
            FundoObra: toDecimal(pickValor(garantias, "fundoObra")),
            FundoReserva: toDecimal(pickValor(garantias, "fundoReserva")),
            FundoLiquidez: toDecimal(pickValor(garantias, "fundoLiquidez")),
            Seguro: toDecimal(pickValor(garantias, "seguro")),
            Sobregarantia: toDecimal(
                pickValor(garantias, "sobreGarantia") ??
                pickValor(garantias, "sobregarantias")
            ),
            PatrimonioSeparado: toDecimal(pickValor(garantias, "patrimonioSeparado")),
        },
        Covenants: covenantsRows.map((row) => ({
            DataValidadeInicial: toISODate(row.dataValidadeInicial),
            DataValidadeFinal: toISODate(row.dataValidadeFinal),
            TipoCovenant: row.tipoCovenant || null,
            Clausula: row.clausula || null,
            Covenant_Numerador: row.numerador || null,
            Covenant_Denominador: row.denominador || null,
            Limite: row.limite || null,
            Observacao: row.observacao || null,
        })),
    }

    return caracteristicaPayload
}

export const mapCadastroTemporarioResponseToState = (data = {}) => {
    const caracteristica = ensureObject(pickCaseInsensitive(data, "Caracteristica"))
    const oferta = ensureObject(pickCaseInsensitive(data, "Oferta"))
    const garantias = ensureObject(pickCaseInsensitive(data, "Garantias"))
    const ltv = ensureObject(pickCaseInsensitive(data, "LTV"))
    const covenantsRaw = pickCaseInsensitive(data, "Covenants")
    const covenants = Array.isArray(covenantsRaw) ? covenantsRaw : []
    const cetipValue = ensureUpperString(data.CETIP)

    const caracteristicasState = {
        cetip: cetipValue,
        versaoDocumento: ensureString(data.VersaoDocumento),
        emissao: ensureString(caracteristica.IDEmissao || data.IDEmissao),
        dataReferencia: formatDateBr(caracteristica.DataReferencia),
        dataDocumento: formatDateBr(caracteristica.DataDocumento),
        isin: ensureString(caracteristica.ISIN),
        numeroSerie: ensureString(caracteristica.NumeroSerie),
        numeroEmissao: ensureString(caracteristica.NumeroEmissao),
        tipoAtivo: ensureString(caracteristica.TipoAtivo),
        tipoOferta: ensureString(caracteristica.TipoOferta),
        siteFiduciario: ensureString(caracteristica.SiteFiduciario),
        frequenciaJuros: mapFrequencyToInt(caracteristica.FrequenciaJuros),
        frequenciaAmortizacao: mapFrequencyToInt(caracteristica.FrequenciaAmortizacao),
        resgateAntecipado: fromRadioLike(caracteristica.ResgateAntecipado),
        dataEmissao: formatDateBr(caracteristica.DataEmissao),
        dataIntegralizacao: formatDateBr(caracteristica.DataPrimeiraIntegralizacao),
        dataIntegralizacaoManual: formatDateBr(caracteristica.DataPrimeiraIntegralizacao) !== "",
        dataCarencia: formatDateBr(caracteristica.DataCarenciaResgate),
        dataCarenciaManual: formatDateBr(caracteristica.DataCarenciaResgate) !== null,
        dataVencimento: formatDateBr(caracteristica.DataVencimento),
        dataResgateAntecipado: formatDateBr(caracteristica.DataResgateAntecipado),
        dataVencimentoAntecipado: formatDateBr(caracteristica.DataVencimentoAntecipado),
        indexador: ensureString(caracteristica.Indexador),
        remuneracao: toDecimalComma(caracteristica.Remuneracao),
        puEmissao: toDecimalComma(caracteristica.PUEmissao),
        spreadEmissao: toDecimalComma(caracteristica.SpreadEmissao),
        prazoMedio: toDecimalComma(caracteristica.PrazoMedioEmissao),
        prazoEmissao: toDecimalComma(caracteristica.PrazoEmissao),
        qtdEmitida: toDecimalComma(caracteristica.QtdEmitida),
        valorSerie: toDecimalComma(caracteristica.ValorSerie),
        valorTotalEmissao: toDecimalComma(caracteristica.ValorTotalEmissao),
        cotaSubordinacao: ensureString(caracteristica.CotaSubordinacao),
        cascataPagamento: ensureString(caracteristica.CascataPagamento),
        liquidezDiaria: toInt(caracteristica.LiquidezDiaria),
        conversibilidade: fromRadioLike(caracteristica.Conversibilidade),
        perpetuidade: fromRadioLike(caracteristica.Perpetuidade),
        incentivada: fromRadioLike(caracteristica.Incentivada),
        green: fromRadioLike(caracteristica.Green),
        lei12431: fromRadioLike(caracteristica.Lei12431),
        lei14801: fromRadioLike(caracteristica.Lei14801),
        prePagamento: fromRadioLike(caracteristica.PrePagamento),
        garantiasReais: fromRadioLike(caracteristica.GarantiasReais),
        garantiasFidejussorias: fromRadioLike(caracteristica.GarantiasFidejussorias),
        mitigadorRisco: fromRadioLike(caracteristica.MitigadorRiscoCredito),
        covenants: fromRadioLike(caracteristica.Covenants),
        repactuacaoCredito: fromRadioLike(caracteristica.RepactuacaoCredito),
        agenciaRating: ensureString(caracteristica.CPFCNPJ_AgenciaRating),
        auditoria: ensureString(caracteristica.CPFCNPJ_Auditoria),
        contaRecebedora: ensureString(caracteristica.CPFCNPJ_IFContaRecebedora),
        registrador: ensureString(caracteristica.CPFCNPJ_Registrador),
        depositario: ensureString(caracteristica.CPFCNPJ_Depositario),
        custodiante: ensureString(caracteristica.CPFCNPJ_Custodiante),
        emissor: ensureString(caracteristica.CPFCNPJ_Emissor),
        escriturador: ensureString(caracteristica.CPFCNPJ_Escriturador),
        cedente: ensureString(caracteristica.CPFCNPJ_Cedente),
        agenteFiduciario: ensureString(caracteristica.CPFCNPJ_AgenteFiduciario),
        devedor: ensureString(caracteristica.CPFCNPJ_Devedor),
        resumoDevedor: ensureString(caracteristica.ResumoDevedor),
        setorDevedor: ensureString(caracteristica.Setor),
        subSetorDevedor: ensureString(caracteristica.Subsetor),
        destinacaoRecursos: ensureString(caracteristica.DestinacaoRecursos),
        cobrigacao: ensureString(caracteristica.Coobrigacao),
    }

    const ofertaState = {
        distribuicao: ensureString(oferta.Distribuicao),
        linkSre: ensureString(oferta.LinkSRE),
        situacaoDistribuicao: ensureString(oferta.SituacaoDistribuicao),
        situacaoOferta: ensureString(oferta.SituacaoOferta),
        tipoMercado: ensureString(oferta.TipoMercado),
        tipoRito: ensureString(oferta.TipoRito),
        bookbuilding: ensureString(oferta.Bookbuilding),
        remuneracaoBookbuilding: ensureString(oferta.RemuneracaoBookbuilding),
        regimeDistribuicao: ensureString(oferta.RegimeDistribuicao),
        investidorGeralInicial: ensureString(oferta.PublicoAlvoInvestidorGeralInicial),
        investidorQualificadoInicial: ensureString(oferta.PublicoAlvoInvestidorQualificadoInicial),
        investidorProfissionalInicial: ensureString(oferta.PublicoAlvoInvestidorProfissionalInicial),
        investidorGeralFinal: ensureString(oferta.PublicoAlvoInvestidorGeralFinal),
        investidorQualificadoFinal: ensureString(oferta.PublicoAlvoInvestidorQualificadoFinal),
        investidorProfissionalFinal: ensureString(oferta.PublicoAlvoInvestidorProfissionalFinal),
        dataRequerimento: formatDateBr(oferta.DataRequerimento),
        dataRegistro: formatDateBr(oferta.DataRegistro),
        dataInicioOferta: formatDateBr(oferta.DataInicioOferta),
        dataEncerramentoMax: formatDateBr(oferta.DataEncerramentodaOfertaMaximo),
        dataEncerramentoEfetivo: formatDateBr(oferta.DataEncerramentodaOfertaEfetivo),
        dataInicioReserva: formatDateBr(oferta.DataInicioReserva),
        dataEncerramentoReserva: formatDateBr(oferta.DatasEncerramentoReserva),
        dataBookbuilding: formatDateBr(oferta.DataBookbuilding),
        dataLockupIGparaIG: formatDateBr(oferta.DataLockupIGparaIG),
        dataLockupIGparaIQ: formatDateBr(oferta.DataLockupIGparaIQ),
        dataLockupIGparaIP: formatDateBr(oferta.DataLockupIGparaIP),
        dataLockupIQparaIG: formatDateBr(oferta.DataLockupIQparaIG),
        dataLockupIQparaIQ: formatDateBr(oferta.DataLockupIQparaIQ),
        dataLockupIQparaIP: formatDateBr(oferta.DataLockupIQparaIP),
        dataLockupIPparaIG: formatDateBr(oferta.DataLockupIPparaIG),
        dataLockupIPparaIQ: formatDateBr(oferta.DataLockupIPparaIQ),
        dataLockupIPparaIP: formatDateBr(oferta.DataLockupIPparaIP),
        coordLider: ensureString(oferta.CPFCNPJ_CoordenadorLider),
        coord1: ensureString(oferta.CPFCNPJ_Coordenador1),
        coord2: ensureString(oferta.CPFCNPJ_Coordenador2),
        coord3: ensureString(oferta.CPFCNPJ_Coordenador3),
        coord4: ensureString(oferta.CPFCNPJ_Coordenador4),
        coord5: ensureString(oferta.CPFCNPJ_Coordenador5),
        coord6: ensureString(oferta.CPFCNPJ_Coordenador6),
        coord7: ensureString(oferta.CPFCNPJ_Coordenador7),
    }

    const garantiasState = {
        tipoLastro: ensureString(garantias.TipoLastro),
        contaVinculada: fromRadioLike(garantias.ContaVinculada),
    }

    const garantiasMap = {
        alienacao: "AlienacaoFiduciaria",
        cessao: "CessaoFiduciaria",
        fiancaBancaria: "FiancaBancaria",
        hipoteca: "Hipoteca",
        penhor: "Penhor",
        avalista: "Avalista",
        fianca: "Fianca",
        fundoObra: "FundoObra",
        fundoReserva: "FundoReserva",
        fundoLiquidez: "FundoLiquidez",
        sobregarantias: "Sobregarantia",
        sobreGarantia: "Sobregarantia",
        seguro: "Seguro",
        cotasInferiores: "CotasInferiores",
        patrimonioSeparado: "PatrimonioSeparado",
    }

    const ltvMap = {
        ltvNumerador: "LTVNumerador",
        ltvDenominador: "LTVDenominador",
        ltv: "LTV",
        cotasInferiores: "CotasInferiores",
        alienacao: "AlienacaoFiduciaria",
        cessao: "CessaoFiduciaria",
        fiancaBancaria: "FiancaBancaria",
        hipoteca: "Hipoteca",
        penhor: "Penhor",
        avalista: "Avalista",
        fianca: "Fianca",
        fundoObra: "FundoObra",
        fundoReserva: "FundoReserva",
        fundoLiquidez: "FundoLiquidez",
        seguro: "Seguro",
        sobregarantias: "Sobregarantia",
        sobreGarantia: "Sobregarantia",
        patrimonioSeparado: "PatrimonioSeparado",
    }

    Object.entries(garantiasMap).forEach(([stateKey, apiKey]) => {
        const garantiaValor = mapGarantiasField(garantias, apiKey)
        const ltvValor = mapGarantiasField(ltv, ltvMap[stateKey])

        const hasAny = Boolean(garantiaValor || ltvValor)

        garantiasState[`${stateKey}Check`] = hasAny
        garantiasState[stateKey] = garantiaValor
        garantiasState[`${stateKey}Valor`] = toDecimalComma(ltvValor)
    })

    garantiasState.ltvNumerador = toDecimalComma(ltv.LTVNumerador)
    garantiasState.ltvDenominador = toDecimalComma(ltv.LTVDenominador)
    garantiasState.ltv = toDecimalComma(ltv.LTV)


    const covenantsRows = mapCovenantsToRows(covenants)

    return {
        caracteristicasState,
        ofertaState,
        garantiasState,
        covenantsRows,
        cadastroTemporarioInfo: {
            cetip: cetipValue,
            versaoDocumento: ensureString(data.VersaoDocumento),
            id: ensureString(data.ID || data.Id || ""),
        },
    }
}

export const mapCadastroTemporarioParametros = (parametrosState = {}) => {
    const parametros = ensureObject(parametrosState)
    const caracteristicas = ensureObject(parametros.caracteristicas)
    const datas = ensureObject(parametros.datas)
    const jurosCorrecao = ensureObject(parametros.jurosCorrecao)
    const fatorArredondamento = ensureObject(parametros.fatorArredondamento)

    return {
        DataReferencia: toISODate(caracteristicas.dataReferencia),
        TaxaJuros: toDecimal(caracteristicas.taxaJuros) ?? null,
        TipoAtivo: caracteristicas.tipoAtivo || null,
        Indexador: caracteristicas.indexador || null,
        LiquidezDiaria: toInt(caracteristicas.liquidezDiaria),

        ValorNominal: toDecimal(datas.valorNominal) ?? null,
        DataEmissao: toISODate(datas.dataEmissao),
        DataPrimeiraIntegralizacao: toISODate(datas.dataIntegralizacao),
        DataVencimento: toISODate(datas.dataVencimento),
        DataRentabilidadeInicio: toISODate(datas.dataRentabilidadeInicio),
        DataRentabilidadeFim: toISODate(datas.dataRentabilidadeFim),
        FrequenciaJuros: mapFrequencyToInt(datas.frequenciaJuros),
        FrequenciaAmortizacao: mapFrequencyToInt(datas.frequenciaAmortizacao),
        DataInicioPagamentoJuros: toISODate(datas.dataInicioPagamentoJuros),
        DataInicioPagamentoAmortizacao: toISODate(datas.dataInicioPagamentoAmortizacao),
        DataCarenciaRecompra: toISODate(datas.dataCarenciaRecompra),
        FrequenciaRecompra: toInt(datas.frequenciaRecompra) ?? null,

        IndexadorCorrecaoPercentual: toDecimal(jurosCorrecao.indexadorCorrecaoPercentual),
        Defasagem: toInt(jurosCorrecao.defasagem) ?? null,
        CalculoDiasIndexador: jurosCorrecao.calculoDiasIndexador,
        FormulaIndexador: jurosCorrecao.formulaIndexador,
        CalculoDias: jurosCorrecao.calculoDias,
        JurosCapitalizacao: jurosCorrecao.jurosCapitalizacao,
        PeriodicidadeAtualizacaoMonetaria: toInt(jurosCorrecao.periodicidadeAtualizacaoMonetaria),
        MesAniversarioAnualSemestral: jurosCorrecao.mesAniversarioAnualSemestral,
        ProjecaoInflacao: toInt(jurosCorrecao.projecaoInflacao),
        InflacaoNegativa: toInt(jurosCorrecao.inflacaoNegativa),
        RecalculoProjecao: toInt(jurosCorrecao.recalculoProjecao),
        AplicacaoAmortizacao: jurosCorrecao.aplicacaoAmortizacao,
        CorrigeDataEvento: jurosCorrecao.corrigeDataEvento,

        CalculoFatorJuros: fatorArredondamento.calculoFatorJuros,
        CasasFatorJuros: toInt(fatorArredondamento.casasFatorJuros) ?? null,
        CalculoJuros: fatorArredondamento.calculoJuros,
        CasasJuros: toInt(fatorArredondamento.casasJuros) ?? null,
        CalculoFatorCorrecao: fatorArredondamento.calculoFatorCorrecao,
        CasasFatorCorrecao: toInt(fatorArredondamento.casasFatorCorrecao) ?? null,
        CalculoCorrecao: fatorArredondamento.calculoCorrecao,
        CasasCorrecao: toInt(fatorArredondamento.casasCorrecao) ?? null,
        CalculoFatorJurosCDI: fatorArredondamento.calculoFatorJurosCDI,
        CasasFatorJurosCDI: toInt(fatorArredondamento.casasFatorJurosCDI) ?? null,
        CalculoVNA: fatorArredondamento.calculoVNA,
        CasasVNA: toInt(fatorArredondamento.casasVNA) ?? null,
        CalculoFatorDesconto: fatorArredondamento.calculoFatorDesconto,
        CasasFatorDesconto: toInt(fatorArredondamento.casasFatorDesconto) ?? null,
        CalculoFatorExpectativa: fatorArredondamento.calculoFatorExpectativa,
        CasasFatorExpectativa: toInt(fatorArredondamento.casasFatorExpectativa) ?? null,
        CalculoPU: fatorArredondamento.calculoPU,
        CasasPU: toInt(fatorArredondamento.casasPU) ?? null,
    }
}

export const mapCaracteristicaParametros = (api) => {
    if (!api) return {}

    return {
        emissao: api.IDEmissao ?? "",
        dataReferencia: formatDateBr(api.DataReferencia),
        indexador: api.Indexador,
        tipoAtivo: api.TipoAtivo ?? "DEB",
        taxaJuros: api.TaxaJuros ?? 0,
        liquidezDiaria: toInt(api.LiquidezDiaria) ?? 0,

        dataEmissao: formatDateBr(api.DataEmissao),
        dataIntegralizacao: formatDateBr(api.DataPrimeiraIntegralizacao),
        dataVencimento: formatDateBr(api.DataVencimento),
        frequenciaJuros: mapFrequencyToInt(api.FrequenciaJuros) ?? 1,
        frequenciaAmortizacao: mapFrequencyToInt(api.FrequenciaAmortizacao) ?? 1,
    }
}

export const mapParametrosToForm = (api) => {
    if (!api) return {}

    return {
        caracteristicas: {
            cetip: api.CETIP ?? "",
            versaoDocumento: api.VersaoDocumento ?? "",
            emissao: api.IDEmissao ?? "",
            dataReferencia: formatDateBr(api.DataReferencia),
            indexador: api.Indexador,
            tipoAtivo: api.TipoAtivo ?? "",
            taxaJuros: api.TaxaJuros ?? 0,
            liquidezDiaria: api.LiquidezDiaria ?? 0,
            cpfCnpjDevedor: api.cpfCnpjDevedor,
        },

        datas: {
            valorNominal: api.ValorNominal ?? 0,
            dataEmissao: formatDateBr(api.DataEmissao),
            dataIntegralizacao: formatDateBr(api.DataPrimeiraIntegralizacao),
            dataVencimento: formatDateBr(api.DataVencimento),
            dataRentabilidadeInicio: formatDateBr(api.DataRentabilidadeInicio),
            dataRentabilidadeInicioManual: formatDateBr(api.DataRentabilidadeInicio) !== null,
            dataRentabilidadeFim: formatDateBr(api.DataRentabilidadeFim),
            dataRentabilidadeFimManual: formatDateBr(api.DataRentabilidadeFim) !== null,
            frequenciaJuros: api.FrequenciaJuros ?? 1,
            frequenciaAmortizacao: api.FrequenciaAmortizacao ?? 1,
            dataInicioPagamentoJuros: formatDateBr(api.DataInicioPagamentoJuros),
            dataInicioPagamentoAmortizacao: formatDateBr(api.DataInicioPagamentoAmortizacao),
            dataCarenciaRecompra: formatDateBr(api.DataCarenciaRecompra),
            frequenciaRecompra: api.FrequenciaRecompra ?? 0,
        },
        jurosCorrecao: {
            indexadorCorrecaoPercentual: api.IndexadorCorrecaoPercentual ?? "",
            defasagem: api.Defasagem ?? 0,
            calculoDiasIndexador: api.CalculoDiasIndexador ?? "",
            formulaIndexador: api.FormulaIndexador ?? "",
            calculoDias: api.CalculoDias ?? "",
            jurosCapitalizacao: api.JurosCapitalizacao ?? "",
            periodicidadeAtualizacaoMonetaria: api.PeriodicidadeAtualizacaoMonetaria ?? "",
            mesAniversarioAnualSemestral: api.MesAniversarioAnualSemestral ?? "",
            projecaoInflacao: api.ProjecaoInflacao ?? 0,
            inflacaoNegativa: api.InflacaoNegativa ?? "",
            recalculoProjecao: api.RecalculoProjecao ?? "",
            aplicacaoAmortizacao: api.AplicacaoAmortizacao ?? "",
            corrigeDataEvento: api.CorrigeDataEvento ?? "",
        },
        fatorArredondamento: {
            calculoFatorJuros: api.CalculoFatorJuros ?? "",
            casasFatorJuros: api.CasasFatorJuros ?? 0,
            calculoJuros: api.CalculoJuros ?? "",
            casasJuros: api.CasasJuros ?? 0,
            calculoFatorCorrecao: api.CalculoFatorCorrecao ?? "",
            casasFatorCorrecao: api.CasasFatorCorrecao ?? 0,
            calculoCorrecao: api.CalculoCorrecao ?? "",
            casasCorrecao: api.CasasCorrecao ?? 0,
            calculoFatorJurosCDI: api.CalculoFatorJurosCDI ?? "",
            casasFatorJurosCDI: api.CasasFatorJurosCDI ?? 0,
            calculoVNA: api.CalculoVNA ?? "",
            casasVNA: api.CasasVNA ?? 0,
            calculoFatorDesconto: api.CalculoFatorDesconto ?? "",
            casasFatorDesconto: api.CasasFatorDesconto ?? 0,
            calculoFatorExpectativa: api.CalculoFatorExpectativa ?? "",
            casasFatorExpectativa: api.CasasFatorExpectativa ?? 0,
            calculoPU: api.CalculoPU ?? "",
            casasPU: api.CasasPU ?? 0,
        }
    }
}
